import { useEffect, useState } from "react";
import { shownKeys, useSaveVisibility, type Users } from "../../api/admin";
import { Card } from "../../components/ui";

/** Which screens each role is shown. A box is drawn only where the registry
 * permits the pair: a dash is a screen that role may never open, which no
 * tick here could change. Presentation only -- the screen's own guard still
 * decides who may open it. */
export function VisibilityMatrix({ data }: { data: Users }) {
  const save = useSaveVisibility();
  const [matrix, setMatrix] = useState(data.visible);
  useEffect(() => setMatrix(data.visible), [data.visible]);
  const changed = JSON.stringify(matrix) !== JSON.stringify(data.visible);
  return (
    <Card
      title="Who sees which screen"
      sub="Hiding a screen removes its link. The server still decides what each role may open."
      className="visibility-card"
    >
      <div className="visibility-scroll">
        <table className="data-table visibility-table">
          <thead>
            <tr>
              <th>Screen</th>
              {data.roles.map((r) => (
                <th key={r.value} className="centre">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.screens.map((s) => (
              <tr key={s.key}>
                <td>
                  {s.label} <span className="muted mono">{s.href}</span>
                  {s.admin_gated ? <span className="muted"> · admin</span> : null}
                </td>
                {data.roles.map((r) => {
                  const permitted = data.permitted[r.value]?.includes(s.key) ?? false;
                  return (
                    <td key={r.value} className="centre">
                      {permitted ? (
                        <input
                          type="checkbox"
                          aria-label={`${r.label} sees ${s.label}`}
                          checked={matrix[r.value]?.[s.key] ?? true}
                          disabled={!data.may_administer}
                          onChange={(e) =>
                            setMatrix((m) => ({ ...m, [r.value]: { ...m[r.value], [s.key]: e.target.checked } }))
                          }
                        />
                      ) : (
                        <span className="muted" title="This role may never open this screen">
                          —
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.may_administer ? (
        <div className="admin-form-actions">
          {save.isError ? <p className="notice bad">{save.error.message}</p> : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !changed}
            onClick={() => save.mutate(shownKeys(matrix))}
          >
            Save visibility
          </button>
        </div>
      ) : null}
    </Card>
  );
}
